import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import DealsTable, {
  excludeBuiltinOwnedDealFields,
  getDealsCurrencyGroupByOptions,
  getDealsHiddenByDefaultColumnIds,
  getDealsSortMenuExclusions,
  useDealsCurrencyColumnLabels
} from "@/pages/deals/components/DealsTable/DealsTable";
import { useTermsColumnsVisibleByDefault } from "@/entities/dataTables/currencyColumns";
import {
  dealsFilterPickerExcludedKeys,
  useDealsFilterOptions,
  getDealsSortByOptions
} from "@/pages/deals/components/DealsToolbar";
import Spinner from "@/components/ui/spinner";
import { dealsColumnKeys, normalizeDealsGroupBy } from "@/pages/deals/helpers";
import { Filters } from "@/services/api/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  mergeMissingDefaultColumns
} from "@/entities/dataTables/helpers";
import { useEntityFields, useFieldVisibility } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useClosedLostReasons } from "@/entities/settings/deal/hooks";
import { APIFieldItem } from "@/pages/settings/types";
import { getPartnerBaseFilters } from "@/pages/partners/helpers";
import { useResetFilteredDeals } from "@/pages/deals/hooks";
import { useUsersMe } from "@/entities/users/hooks";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import PartnerTabEmptyState from "./PartnerTabEmptyState";

// Distinct from the standalone "deals-table" so the scoped tab keeps its own
// column/view state, while getPageNameFromTableId still resolves to "deals".
const TABLE_ID = "deals-partner-table";

interface ContentProps {
  customFields: APIFieldItem[];
  baseFilters: Filters;
  partnerId: string;
}

// Rendered only once the partner field has resolved, so `filters` can seed
// directly to the partner scope — the first fetch is already filtered and the
// table never flashes the full, unscoped list.
const PartnerDealsTabContent: React.FC<ContentProps> = ({
  customFields,
  baseFilters,
  partnerId
}) => {
  const { t } = useTranslation();
  const { fieldName: arrOrMrrField, label: arrOrMrrLabel } = useArrMrr();
  const { data: userData } = useUsersMe();
  const resetDeals = useResetFilteredDeals();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTableEmpty, setIsTableEmpty] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Filters>(baseFilters);
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: arrOrMrrField, desc: true }
  ]);
  const [groupBy, setGroupByState] = useState<string[]>([]);
  // A view grouped by a replaced option is activated through here.
  const setGroupBy = useCallback(
    (next: string[]) => setGroupByState(normalizeDealsGroupBy(next)),
    []
  );
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.DEALS_TABLE)
  );

  const { filterVisibleColumnKeys } = useFieldVisibility(EntityType.DEAL);
  const activeDealsColumnKeys = useMemo(
    () => filterVisibleColumnKeys(dealsColumnKeys),
    [filterVisibleColumnKeys]
  );
  // This tab seeds its own column state from the same `DealColumn` enum the
  // standalone page uses, so the split money pairs reach it too — it needs the
  // same default-off gate and the same composed headers, or a single-currency
  // account sees every terms column here while the deals page hides them, and
  // the picker labels them by an id that has no translation key.
  const {
    visibleByDefault: termsColumnsVisibleByDefault,
    isResolved: currencyUsageResolved
  } = useTermsColumnsVisibleByDefault();
  const currencyColumnLabels = useDealsCurrencyColumnLabels(
    arrOrMrrLabel,
    arrOrMrrField,
    customFields
  );
  // Same reservation the standalone deals page applies: registry rows a
  // built-in column already renders must not also be offered as custom columns.
  const columnManagementFields = useMemo(
    () => excludeBuiltinOwnedDealFields(customFields),
    [customFields]
  );
  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activeDealsColumnKeys,
        columnManagementFields,
        getPageNameFromTableId(TABLE_ID),
        t,
        arrOrMrrLabel,
        arrOrMrrField,
        getDealsHiddenByDefaultColumnIds(termsColumnsVisibleByDefault),
        currencyColumnLabels,
        true
      ),
    [
      activeDealsColumnKeys,
      columnManagementFields,
      t,
      arrOrMrrLabel,
      arrOrMrrField,
      termsColumnsVisibleByDefault,
      currencyColumnLabels
    ]
  );
  // Opening a view saved without columns empties the layout, so the seeding
  // below re-runs on that too — under the same gate, never ahead of it.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    // Seeding before the currency-usage read settles would bake in the
    // fall-back "show them" answer, which a later "no foreign currency"
    // response can no longer take back.
    if (!currencyUsageResolved) return;
    // Merge rather than seed-only, so a layout saved before the split money
    // columns existed is offered them too — under the same gate, since a column
    // folded in while the read is in flight is folded in visible.
    setColumnsState((prev) =>
      prev.length === 0
        ? defaultColumnsState
        : mergeMissingDefaultColumns(prev, defaultColumnsState)
    );
  }, [defaultColumnsState, isColumnsStateEmpty, currencyUsageResolved]);

  const handleColumnsChange = useCallback((updated?: ColumnData[]) => {
    if (updated) setColumnsState(updated);
  }, []);

  const { data: closedLostReasonsData } = useClosedLostReasons();
  const closedLostReasons = useMemo(
    () => closedLostReasonsData?.closedLostReasons || [],
    [closedLostReasonsData]
  );

  const dealsFilterOptions = useDealsFilterOptions({
    viewType: ViewType.DEALS_TABLE,
    closedLostReasons,
    arrMrrFieldName: arrOrMrrField
  });
  const dealsSortByOptions = getDealsSortByOptions(arrOrMrrField);
  const sortMenuExcludedColumnIds = useMemo(
    () => getDealsSortMenuExclusions(customFields),
    [customFields]
  );

  // Nothing linked yet — search, filters and column display have nothing to act
  // on, so the empty state stands alone. Once filters or a search term are what
  // emptied the table, the toolbar stays so the user can undo them.
  const isPristine = isEmpty(searchTerm) && isEqual(filters, baseFilters);
  const showEmptyState = isPristine && isTableEmpty;

  return (
    <div className="flex flex-col h-full">
      <ToolbarActions
        className={showEmptyState ? "hidden" : "p-2 pr-4"}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={dealsFilterOptions}
        baseFilters={baseFilters}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={getDealsCurrencyGroupByOptions(arrOrMrrField)}
        groupByDateBuckets
        sorting={sorting}
        onSortingChange={onSortingChange}
        sortByOptions={dealsSortByOptions}
        sortMenuExcludedColumnIds={sortMenuExcludedColumnIds}
        filterPickerExcludedKeys={dealsFilterPickerExcludedKeys}
        defaultSortByValue={arrOrMrrField}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={ViewType.DEALS_TABLE}
        onColumnsChange={handleColumnsChange}
        tableId={TABLE_ID}
        customFields={customFields}
      />
      <DealsTable
        filters={filters}
        setFilters={setFilters}
        filterOptions={dealsFilterOptions}
        searchTerm={searchTerm}
        groupBy={groupBy}
        sorting={sorting}
        onSortingChange={onSortingChange}
        tableId={TABLE_ID}
        selectedView={selectedView}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        customFields={customFields}
        hasActiveFilters={!isEqual(filters, baseFilters)}
        onClearFilters={() => setFilters(baseFilters)}
        onClearSearch={() => setSearchTerm("")}
        onEmptyChange={setIsTableEmpty}
        customEmptyState={
          isPristine ? (
            <PartnerTabEmptyState
              tab="deals"
              onAdd={() => setIsCreateModalOpen(true)}
            />
          ) : undefined
        }
        className="h-full"
      />
      {userData ? (
        <CreateDealModal
          isOpen={isCreateModalOpen}
          setIsOpen={setIsCreateModalOpen}
          userData={userData}
          partnerId={partnerId}
          onDealCreated={resetDeals}
        />
      ) : null}
    </div>
  );
};

interface Props {
  partnerId: string;
}

const PartnerDealsTab: React.FC<Props> = ({ partnerId }) => {
  const { data: entityFieldsResponse } = useEntityFields(EntityType.DEAL);
  const customFields = useMemo(
    () => entityFieldsResponse?.fields || [],
    [entityFieldsResponse]
  );
  const baseFilters = useMemo(
    () => getPartnerBaseFilters(customFields, partnerId),
    [customFields, partnerId]
  );
  // Wait for the entity fields so the content mounts with the partner scope
  // already resolved and seeds it as the initial filter.
  if (!entityFieldsResponse) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PartnerDealsTabContent
      customFields={customFields}
      baseFilters={baseFilters}
      partnerId={partnerId}
    />
  );
};

export default PartnerDealsTab;
